import React from 'react';

import { alertCircle, check } from '@/assets/icons';
import { SetlistSummaryCalendar } from '@/components/SetlistSummaryCard';
import { TrendIcon } from '@/components/TrendIcon';
import {
	Song,
	SongAnalyisStatus,
	TeamSong,
	TeamSongFeedback,
	TeamSongFeedbackReport,
	Trend,
} from '@/graphql';
import { generateAlbumGradient } from '@/helpers/albumGradient';
import { classNames } from '@/helpers/classNames';
import { deriveTrend } from '@/helpers/deriveTrend';
import { getAcronym } from '@/helpers/textTransform';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { SvgIcon } from '@material-ui/core';

import { Chip } from '../Chip';
import { Loader } from '../Loader';
import { SongThemeChip } from '../SongThemeChip';
import styles from './SongCard.module.scss';

export type SongCardSong = Pick<
	Song,
	| 'name'
	| 'artist'
	| 'artworkUrl'
	| 'recordedKey'
	| 'recommendedKey'
	| 'analysisStatus'
> &
	Pick<TeamSong, 'defaultKey' | 'rotationStatus'> & {
		feedback?:
			| (Pick<TeamSongFeedback, 'overallScore'> & {
					teamSongFeedbackReports: Pick<
						TeamSongFeedbackReport,
						| 'startAt'
						| 'meanPersonalScore'
						| 'meanTeamScore'
						| 'meanCongregationalScore'
					>[];
			  })
			| null;
		new?: TeamSong['new'];
	};

export interface SongCardProps {
	song: SongCardSong;
	className?: string;
	onClick?: () => void;
	onSelect?: () => void;
	selectable?: boolean;
	trailingComponent?: React.ReactNode;
	checked?: boolean;
	light?: boolean;
	index?: number;
	isDisabled?: boolean;
	displayKey?: boolean;
	displayTrend?: boolean;
	displayOverallScore?: boolean;
	displayLastPlayed?: boolean;
	lastPlayedISO?: string;

	// Rotation Status
	rotationStatusOnClick?: (
		e: React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => void;
	displayRotationStatus?: boolean;
	rotationStatusWarning?: boolean;

	albumArtSize?: 'sm' | 'md';
	isLoading?: boolean;
	removed?: boolean;
	negativeSpacing?: boolean;
	hasHover?: boolean;
	trend?: Trend;
}

const isCapacitor = Capacitor.isNativePlatform();

export const SongCard: React.FC<SongCardProps> = ({
	song,
	className,
	displayKey = true,
	displayTrend = false,
	displayOverallScore = false,
	displayLastPlayed = false,
	displayRotationStatus = false,
	rotationStatusWarning,
	albumArtSize = 'md',
	onClick = () => null,
	onSelect = () => null,
	rotationStatusOnClick = () => void 0,
	isDisabled = false,
	selectable = false,
	checked = false,
	light = false,
	index,
	isLoading,
	removed,
	negativeSpacing,
	hasHover = true,
	trailingComponent,
	trend: trendProp,
	lastPlayedISO,
}) => {
	const songCardClasses = classNames([
		styles['song-card'],
		!isCapacitor && !removed && hasHover && styles['hover'],
		className || '',
		removed && styles['removed'],
		negativeSpacing && styles['negative-spacing'],
		isDisabled && styles['disabled'],
	]);

	const currentSong = isLoading
		? {
				name: 'Loading...',
				artist: 'Loading...',
				recordedKey: 'L',
				artworkUrl: '',
				feedback: null,
				defaultKey: '',
				recommendedKey: '',
		  }
		: song;

	const albumGradient =
		!isLoading && !currentSong.artworkUrl
			? generateAlbumGradient(currentSong.name)
			: null;

	const trend =
		trendProp ??
		(currentSong.feedback
			? deriveTrend(
					currentSong.feedback.teamSongFeedbackReports.slice(-5),
			  )
			: undefined);
	const hasScore = !!currentSong.feedback?.overallScore;
	const overallScore = hasScore
		? Math.round(
				((currentSong.feedback?.overallScore || 0) + Number.EPSILON) *
					10,
		  ) / 10
		: null;
	const lastPlayed =
		lastPlayedISO ??
		currentSong.feedback?.teamSongFeedbackReports.slice(-1)[0]?.startAt;

	return (
		<div className={songCardClasses}>
			{/* Left side */}
			<div
				className={styles['clickable']}
				onClick={() => (!isLoading ? onClick() : null)}
			>
				<div
					className={classNames([
						styles['cover-image'],
						isLoading && styles['loading'],
						albumArtSize && styles[`size-${albumArtSize}`],
					])}
					style={albumGradient ? albumGradient.style : undefined}
				>
					{isLoading ? (
						<Loader />
					) : currentSong.artworkUrl ? (
						<img src={currentSong.artworkUrl} />
					) : (
						<span
							style={
								albumGradient
									? { color: albumGradient.textColor }
									: undefined
							}
						>
							{getAcronym(currentSong.name)}
						</span>
					)}
					{!!index && <div className={styles['index']}>{index}</div>}
					{/* HIDE ROTATION STATUS ON ALBUM ART FOR NOW
					<div
						className={classNames([
							song.rotationStatus && styles['rotation'],
							getRotationStatusClassName(song.rotationStatus),
						])}
					/>
					WILL NEED TO REFACTOR OR REMOVE AFTER SONG CARD UI UPDATES*/}
				</div>
				<div className={styles['middle']}>
					<div className={styles['title']}>{currentSong.name}</div>
					<div className={styles['artist']}>{currentSong.artist}</div>
				</div>
				{currentSong.new ? <Chip color="light-gray">New</Chip> : null}
				{currentSong.analysisStatus &&
				currentSong.analysisStatus === SongAnalyisStatus.Waiting ? (
					<Chip color="gradient">Analysing</Chip>
				) : null}
			</div>

			{/* Additional items */}
			<div
				className={styles['additional-items']}
				onClick={() => {
					if (selectable) {
						Haptics.impact({ style: ImpactStyle.Medium });
						return onSelect();
					}
					onClick();
				}}
			>
				{displayTrend && trend && (
					<div className={styles['trend']}>
						<TrendIcon trend={trend} />
					</div>
				)}
				{displayOverallScore && (
					<div
						className={classNames([
							styles['overall-score'],
							hasScore
								? styles[getOverallScoreClass(overallScore!)]
								: styles['score-none'],
						])}
					>
						{hasScore ? overallScore : '—'}
					</div>
				)}
				{displayKey && (
					<div
						className={classNames([
							styles['key'],
							light && styles['light'],
						])}
					>
						{currentSong.defaultKey ||
							currentSong.recommendedKey ||
							currentSong.recordedKey ||
							'-'}
					</div>
				)}
				{displayRotationStatus && (
					<SongThemeChip
						themeTag={currentSong.rotationStatus}
						leadingComponent={
							rotationStatusWarning ? (
								<SvgIcon
									viewBox="0 0 16 16"
									component={alertCircle}
								/>
							) : undefined
						}
						variant="rounded"
						color="color-scheme"
						onClick={rotationStatusOnClick}
						className={classNames([styles['rotation-status-chip']])}
					/>
				)}
				{displayLastPlayed &&
					(lastPlayed ? (
						<div className={styles['last-played']}>
							<SetlistSummaryCalendar
								size="md"
								startAtISO={lastPlayed || '0'}
								isLoading={!lastPlayed}
								isDisabled={!lastPlayed}
							/>
						</div>
					) : null)}
				{selectable && (
					<div className={styles['selectable']}>
						<div
							className={classNames([
								styles['card-icon-container'],
								checked && styles['checked'],
							])}
						>
							{checked && (
								<SvgIcon
									component={check}
									classes={{
										root: styles['card-icon'],
									}}
								/>
							)}
						</div>
					</div>
				)}
				{!!trailingComponent && trailingComponent}
			</div>
		</div>
	);
};

function getOverallScoreClass(score: number): string {
	switch (true) {
		case score <= 1.5:
			return 'score-1.5';
		case score <= 2.5:
			return 'score-2.5';
		case score <= 2.9:
			return 'score-2.9';
		case score <= 3.5:
			return 'score-3.5';
		case score <= 4.5:
			return 'score-4.5';
		case score > 4.5:
			return 'score-5';
		default:
			return 'score-1.5';
	}
}
